// Smart, type-aware eBay title composer.
// Pure module — no Deno/web imports so it can be duplicated to `src/lib/ebayTitleComposer.ts`
// for live preview in the listing dialog. Keep the two copies in sync.
//
// Strategy: greedily pack the 80-char title budget with the highest-signal
// tokens buyers actually search for, branched by product_type / category.

export interface TitleComposerItem {
	title?: string | null;
	game?: string | null;
	set_name?: string | null;
	set_code?: string | null;
	card_number?: string | null;
	rarity?: string | null;
	finish?: string | null;
	finish_label?: string | null;
	language?: string | null;
	condition?: string | null;
	slab_grade?: number | string | null;
	grading_company?: string | null;
	certification_number?: string | null;
	is_first_edition?: boolean | null;
	is_reverse_holo?: boolean | null;
	sealed_condition?: string | null;
	category?: string | null;
	product_type?: string | null;
	quantity?: number | null;
	ebay_title_override?: string | null;
}

const MAX = 80;

// ---------- normalization helpers ----------

const CONDITION_LABELS: Record<string, string> = {
	mint: 'Mint',
	near_mint: 'NM',
	nearmint: 'NM',
	'near mint': 'NM',
	lightly_played: 'LP',
	'lightly played': 'LP',
	moderately_played: 'MP',
	'moderately played': 'MP',
	heavily_played: 'HP',
	'heavily played': 'HP',
	damaged: 'DMG',
	poor: 'Poor',
};

const GAME_LABELS: Record<string, string> = {
	pokemon: 'Pokemon',
	'pokémon': 'Pokemon',
	mtg: 'MTG',
	magic: 'MTG',
	'magic the gathering': 'MTG',
	'magic: the gathering': 'MTG',
	yugioh: 'Yu-Gi-Oh',
	'yu-gi-oh': 'Yu-Gi-Oh',
	'yu gi oh': 'Yu-Gi-Oh',
	lorcana: 'Lorcana',
	'disney lorcana': 'Lorcana',
	'one piece': 'One Piece',
	digimon: 'Digimon',
	'dragon ball': 'Dragon Ball',
	'flesh and blood': 'FAB',
	fab: 'FAB',
	weiss: 'Weiss Schwarz',
	'weiss schwarz': 'Weiss Schwarz',
};

const FINISH_LABELS: Record<string, string> = {
	holo: 'Holo',
	holofoil: 'Holo',
	foil: 'Foil',
	nonfoil: '',
	'non-foil': '',
	'non foil': '',
	normal: '',
	reverse_holo: 'Reverse Holo',
	'reverse holo': 'Reverse Holo',
	etched: 'Etched Foil',
	'foil etched': 'Etched Foil',
};

const GRADER_LABELS: Record<string, string> = {
	psa: 'PSA',
	bgs: 'BGS',
	cgc: 'CGC',
	sgc: 'SGC',
	tag: 'TAG',
	ace: 'ACE',
	gma: 'GMA',
	hga: 'HGA',
};

function normGame(g?: string | null): string | null {
	if (!g) return null;
	const k = g.toLowerCase().trim();
	if (GAME_LABELS[k] !== undefined) return GAME_LABELS[k] || null;
	for (const key of Object.keys(GAME_LABELS)) {
		if (k.includes(key)) return GAME_LABELS[key] || null;
	}
	// Sports games stay as-is, title-cased
	return g.trim();
}

const NULLISH_TOKENS = new Set(['none', 'n/a', 'na', 'null', 'undefined', 'unknown', '-', '—']);
function isNullish(v?: string | null): boolean {
	if (!v) return true;
	return NULLISH_TOKENS.has(v.toLowerCase().trim());
}
function clean(v?: string | null): string | null {
	return isNullish(v) ? null : (v as string).trim();
}

function normFinish(f?: string | null, label?: string | null, reverseHolo?: boolean | null): string | null {
	if (reverseHolo) return 'Reverse Holo';
	const rawSrc = clean(label) ?? clean(f);
	if (!rawSrc) return null;
	const raw = rawSrc.toLowerCase();
	if (FINISH_LABELS[raw] !== undefined) return FINISH_LABELS[raw] || null;
	// Free text like "Foil Etched" — keep the input title-cased
	return rawSrc;
}

function normCondition(c?: string | null): string | null {
	if (!c) return null;
	const k = c.toLowerCase().trim().replace(/\s+/g, '_');
	if (CONDITION_LABELS[k]) return CONDITION_LABELS[k];
	const k2 = c.toLowerCase().trim();
	if (CONDITION_LABELS[k2]) return CONDITION_LABELS[k2];
	return null;
}

function normGrader(g?: string | null): string | null {
	if (!g) return null;
	const k = g.toLowerCase().trim();
	return GRADER_LABELS[k] || g.toUpperCase().slice(0, 5);
}

// Strip leading "0"s and any "set-prefix-#" noise from card numbers for title use
function cleanCardNumber(n?: string | null): string | null {
	if (!n) return null;
	const m = String(n).trim().match(/(\d+[a-zA-Z]?)(?:\s*\/\s*\d+)?$/);
	if (!m) return null;
	return m[1].replace(/^0+(?=\d)/, '');
}

// Pull a 4-digit year out of free text (set name, card number, etc.)
function pickYear(...candidates: Array<string | null | undefined>): string | null {
	for (const c of candidates) {
		if (!c) continue;
		const m = String(c).match(/(19|20)\d{2}/);
		if (m) return m[0];
	}
	return null;
}

function detectShape(item: TitleComposerItem): 'slab' | 'sealed' | 'lot' | 'accessory' | 'single' {
	const cat = (item.category || '').toLowerCase();
	const pt = (item.product_type || '').toLowerCase();
	if (item.slab_grade && item.grading_company) return 'slab';
	if (cat.includes('sealed') || item.sealed_condition || /booster|etb|tin|bundle|collection|blister|case|deck|pack|box/.test(pt)) return 'sealed';
	if (pt.includes('lot') || cat.includes('lot') || (item.quantity ?? 0) > 1 && cat.includes('mixed')) return 'lot';
	if (pt.includes('accessor') || cat.includes('accessor') || /sleeve|binder|playmat|deckbox|toploader|case/.test(pt)) return 'accessory';
	return 'single';
}

// ---------- packing ----------

interface Token {
	value: string;
	priority: 0 | 1 | 2 | 3; // 0 = required, lower = higher priority
}

function pack(tokens: Token[]): string {
	// Stable sort by priority
	const ordered = tokens
		.map((t, i) => ({ ...t, i }))
		.sort((a, b) => (a.priority - b.priority) || (a.i - b.i));

	const picked: { value: string; i: number }[] = [];
	const seen = new Set<string>();
	let len = 0;
	for (const t of ordered) {
		const v = t.value.trim();
		if (!v) continue;
		const key = v.toLowerCase();
		// Drop sentinel "none"-style values for non-required tokens
		if (t.priority !== 0 && NULLISH_TOKENS.has(key)) continue;
		// Dedupe — skip if any picked token already contains this token (or vice versa)
		let dup = false;
		for (const p of picked) {
			const pk = p.value.toLowerCase();
			if (pk === key || pk.includes(key) || key.includes(pk)) { dup = true; break; }
		}
		if (dup || seen.has(key)) continue;
		const add = (picked.length ? 1 : 0) + v.length;
		if (len + add > MAX) {
			if (t.priority === 0) {
				// Required token doesn't fit — hard-trim it
				const room = MAX - len - (picked.length ? 1 : 0);
				if (room > 4) {
					picked.push({ value: v.slice(0, room).trimEnd(), i: t.i });
					len += add;
				}
			}
			continue;
		}
		picked.push({ value: v, i: t.i });
		seen.add(key);
		len += add;
	}

	// Restore original order so the title reads naturally
	picked.sort((a, b) => a.i - b.i);
	return picked.map((p) => p.value).join(' ').replace(/\s+/g, ' ').trim();
}

// ---------- branch composers ----------

function composeSingle(item: TitleComposerItem): string {
	const game = normGame(item.game);
	const set = (item.set_name || item.set_code || '').trim() || null;
	const year = pickYear(item.set_name, item.set_code);
	const num = cleanCardNumber(item.card_number);
	const finish = normFinish(item.finish, item.finish_label, item.is_reverse_holo);
	const cond = normCondition(item.condition);
	const name = (item.title || '').trim() || 'Trading Card';

	const tokens: Token[] = [
		{ value: name, priority: 0 },
		{ value: set || '', priority: 1 },
		{ value: num ? `#${num}` : '', priority: 1 },
		{ value: game || '', priority: 2 },
		{ value: finish || '', priority: 2 },
		{ value: item.is_first_edition ? '1st Edition' : '', priority: 2 },
		{ value: year || '', priority: 3 },
		{ value: item.rarity ? String(item.rarity) : '', priority: 3 },
		{ value: cond || '', priority: 3 },
	];
	return pack(tokens);
}

function composeSlab(item: TitleComposerItem): string {
	const game = normGame(item.game);
	const set = (item.set_name || item.set_code || '').trim() || null;
	const year = pickYear(item.set_name, item.set_code);
	const num = cleanCardNumber(item.card_number);
	const finish = normFinish(item.finish, item.finish_label, item.is_reverse_holo);
	const grader = normGrader(item.grading_company);
	const grade = item.slab_grade != null ? String(item.slab_grade) : null;
	const graderGrade = grader && grade ? `${grader} ${grade}` : grader || grade || '';
	const name = (item.title || '').trim() || 'Graded Card';

	const tokens: Token[] = [
		{ value: name, priority: 0 },
		{ value: graderGrade, priority: 0 },
		{ value: set || '', priority: 1 },
		{ value: num ? `#${num}` : '', priority: 1 },
		{ value: year || '', priority: 2 },
		{ value: game || '', priority: 2 },
		{ value: finish || '', priority: 3 },
		{ value: item.rarity ? String(item.rarity) : '', priority: 3 },
		{ value: item.is_first_edition ? '1st Edition' : '', priority: 3 },
	];
	return pack(tokens);
}

function composeSealed(item: TitleComposerItem): string {
	const game = normGame(item.game);
	const set = (item.set_name || item.set_code || '').trim() || null;
	const year = pickYear(item.set_name, item.set_code);
	const pt = (item.product_type || '').trim();
	const ptLabel = pt
		? pt.split(/[_\s-]+/).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
		: '';
	const name = (item.title || '').trim() || ptLabel || 'Sealed Product';

	const tokens: Token[] = [
		{ value: name, priority: 0 },
		{ value: game || '', priority: 1 },
		{ value: set || '', priority: 1 },
		{ value: ptLabel, priority: 2 },
		{ value: 'Factory Sealed', priority: 2 },
		{ value: year || '', priority: 3 },
	];
	return pack(tokens);
}

function composeLot(item: TitleComposerItem): string {
	const game = normGame(item.game);
	const set = (item.set_name || item.set_code || '').trim() || null;
	const qty = item.quantity && item.quantity > 1 ? `Lot of ${item.quantity}` : 'Lot';
	const cond = normCondition(item.condition);
	const name = (item.title || '').trim() || 'Card Lot';

	const tokens: Token[] = [
		{ value: name, priority: 0 },
		{ value: qty, priority: 1 },
		{ value: game || '', priority: 2 },
		{ value: set || '', priority: 2 },
		{ value: cond || '', priority: 3 },
	];
	return pack(tokens);
}

function composeAccessory(item: TitleComposerItem): string {
	const game = normGame(item.game);
	const name = (item.title || '').trim() || 'TCG Accessory';
	const tokens: Token[] = [
		{ value: name, priority: 0 },
		{ value: game || '', priority: 2 },
	];
	return pack(tokens);
}

// ---------- public API ----------

export function composeEbayTitle(item: TitleComposerItem): string {
	// Manual override wins, hard-capped at 80
	if (item.ebay_title_override && item.ebay_title_override.trim()) {
		return item.ebay_title_override.trim().slice(0, MAX);
	}

	const shape = detectShape(item);
	let out: string;
	switch (shape) {
		case 'slab': out = composeSlab(item); break;
		case 'sealed': out = composeSealed(item); break;
		case 'lot': out = composeLot(item); break;
		case 'accessory': out = composeAccessory(item); break;
		default: out = composeSingle(item);
	}
	// Safety net
	if (!out) return (item.title || 'Trading Card').slice(0, MAX);
	return out.slice(0, MAX);
}

export const _TITLE_MAX = MAX;
